/* global axios */

// All knowledge-base paths are forwarded through /api/v1/accounts/:id/proxy/knowledge,
// same pattern as store/modules/knowledge.js and sandbox.js. This module is
// deliberately separate from knowledge.js — that module's createKnowledgeBase
// reads the wrong response field (data.dataset_id instead of data.id) and its
// indexing-status action (in dify.js) hits a broken URL, so onboarding gets
// its own, correct actions rather than inheriting those bugs.
const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

// Confirmed via the existing standalone Knowledge feature (store/modules/knowledge.js
// reads `obj.display_status`; knowledge/viewKnowledge/documents/Document.vue maps its
// values) and its Cypress fixture (documents_status_variety.json), captured from the
// real API: the document status field is `display_status`, with enum values
// `available | indexing | queuing | unavailable | disabled | error`. 'available' is
// the "Ready" terminal state — NOT 'completed'/'ready', which this previously checked
// for and would never have matched, leaving every successful upload to poll for the
// full MAX_POLL_ATTEMPTS and then get wrongly marked 'failed'.
const isIndexingComplete = data => data?.display_status === 'available';

// 'disabled' is also a terminal state (the document was toggled off, not
// mid-indexing) — without it here, a disabled document was neither complete
// nor failed, so pollDocumentStatus kept treating it as still indexing and
// polled it for the full MAX_POLL_ATTEMPTS before wrongly reporting a
// timeout.
const isIndexingFailed = data =>
  ['error', 'unavailable', 'disabled'].includes(data?.display_status);

const MAX_POLL_ATTEMPTS = 60; // ~2 minutes at 2s intervals
const POLL_INTERVAL_MS = 2000;

// Mirrors knowledge.js's appendAdvancedUploadOptions — kept as a separate
// copy rather than a shared import, same reasoning as this module's own
// header comment (deliberately not coupled to knowledge.js). Lightrag-only,
// ignored server-side for dify. Omitting a field keeps the KB's existing
// default for it — only append what the user actually set.
const appendAdvancedUploadOptions = (formData, options) => {
  if (!options) return;
  const { chunkWordSize, chunkOverlapWordSize, delimiter } = options;
  // The API's actual field names are chunk_word_size/chunk_overlap_word_size
  // (confirmed via /openapi.json) — chunk_token_size/chunk_overlap_token_size
  // aren't declared anywhere on this endpoint, so sending those silently
  // dropped the chunk-size override entirely.
  if (
    chunkWordSize !== null &&
    chunkWordSize !== undefined &&
    chunkWordSize !== ''
  ) {
    formData.append('chunk_word_size', chunkWordSize);
  }
  if (
    chunkOverlapWordSize !== null &&
    chunkOverlapWordSize !== undefined &&
    chunkOverlapWordSize !== ''
  ) {
    formData.append('chunk_overlap_word_size', chunkOverlapWordSize);
  }
  if (delimiter) {
    formData.append('delimiter', delimiter);
  }
};

// Uploads are intentionally sequential (one at a time), matching the
// existing knowledge.js pattern — not parallelized to avoid overwhelming
// the server. Implemented via reduce rather than a for-of loop so it stays
// within the project's lint rules around loop constructs.
const uploadSingleFile = async ({
  accountId,
  datasetId,
  file,
  stagedIndex,
  existingDocumentIds,
  commit,
  dispatch,
  advancedUploadOptions,
}) => {
  const localId = `local-${file.name}-${Date.now()}`;
  // size + stagedIndex let the processing modal show a file card and let a
  // retry find the original File again (see retryFailedDocuments).
  commit('ADD_DOCUMENT', {
    id: localId,
    name: file.name,
    size: file.size,
    stagedIndex,
    uploadProgress: 0,
    status: 'uploading',
    error: null,
  });

  const formData = new FormData();
  formData.append('file', file);
  appendAdvancedUploadOptions(formData, advancedUploadOptions);

  try {
    const { data } = await axios.post(
      knowledgeProxy(
        accountId,
        `/chatbot/account/${accountId}/datasets/${datasetId}/documents`
      ),
      formData,
      {
        onUploadProgress: event => {
          if (!event.total) return;
          commit('UPDATE_DOCUMENT', {
            id: localId,
            patch: { uploadProgress: event.loaded / event.total },
          });
        },
      }
    );
    // Confirmed against a live response: the created document's id is
    // nested at data.document.id, not data.id/data.document_id — falling
    // back to the client-generated localId here would silently poll
    // /details with a placeholder the server can never resolve.
    const documentId = data?.document?.id;
    // The Knowledge service answers an upload whose contents duplicate a file
    // already in the knowledge base with that EXISTING document (same id,
    // original name) instead of creating a new one. Tracking it as a second
    // document under the same id would leave it stuck on "indexing" forever —
    // the status updates only ever reach the first document with that id — and
    // the blocking modal would never settle. Report it as skipped instead.
    if (documentId && existingDocumentIds().includes(String(documentId))) {
      commit('UPDATE_DOCUMENT', {
        id: localId,
        patch: {
          status: 'dropped',
          reason: 'duplicate',
          error: 'Same content as another file you added.',
        },
      });
      return false;
    }
    if (!documentId) {
      commit('UPDATE_DOCUMENT', {
        id: localId,
        patch: {
          status: 'failed',
          error: `Upload response for "${file.name}" is missing document.id.`,
        },
      });
      return false;
    }
    commit('UPDATE_DOCUMENT', {
      id: localId,
      patch: { id: documentId, status: 'indexing' },
    });
    // Poll in the background — don't block the upload chain on it.
    dispatch('pollDocumentStatus', { localId: documentId });
    return true;
  } catch (error) {
    const status = error.response?.status;
    let message = `Error uploading "${file.name}".`;
    if (status === 402) {
      message = `Plan limit reached — remove a document or upgrade to add "${file.name}".`;
    } else if (status === 413) {
      message = `"${file.name}" is too large.`;
    }
    commit('UPDATE_DOCUMENT', {
      id: localId,
      patch: { status: 'failed', error: message },
    });
    return false;
  }
};

// This module is registered once for the dashboard SPA's lifetime — it isn't
// torn down when the route's :accountId changes, so datasetId/documents are
// otherwise still sitting in state after switching accounts (there's no
// server-backed "fetch the existing dataset for this account" call here to
// naturally overwrite them, unlike store/modules/sandbox.js's fetchStatus).
// accountId tracks which account the rest of this state belongs to, so
// components can reset it on an account switch and the actions below can
// refuse to act on a dataset left over from a different account.
const createInitialState = () => ({
  accountId: null,
  datasetId: null,
  // Files picked on the file-selection step, held here (not yet uploaded)
  // so the options step can create the dataset — with whatever
  // graph/images/split settings the user chose there — before any upload
  // happens. See stageFiles below.
  stagedFiles: [],
  // [{ id, name, size, stagedIndex, uploadProgress, reason,
  //    status: 'uploading'|'indexing'|'ready'|'failed'|'timed_out'|'dropped',
  //    error }]
  // 'timed_out' is deliberately not 'failed': polling gave up but the
  // document may still finish server-side, so it must not trigger cleanup.
  // 'dropped' is a file that is not in the knowledge base although the user
  // chose it - reason 'removed' (the server no longer has it, typically a
  // duplicate of another file) or 'not_uploaded' (a refresh interrupted the
  // upload) or 'duplicate' (same contents as a file already in the knowledge
  // base - the service hands back the existing document, see uploadSingleFile).
  // Never silently omitted: the modal tells the user.
  documents: [],
  // Documents of a dataset that was deleted because none of them succeeded,
  // kept only so the processing modal can still show what went wrong.
  discardedFailures: [],
  isCreatingDataset: false,
  isUploading: false,
  isError: false,
  // graph_enabled/images_enabled are lightrag-only and creation-time-only
  // (no edit path afterward) — default to true to match the server's own
  // default, same as knowledge.js's create-KB wizard.
  knowledgeGraphEnabled: true,
  knowledgeImagesEnabled: true,
  // Per-upload overrides, lightrag-only, ignored for dify. null/'' means
  // "omit — keep the KB's existing defaults", matching the API's own
  // omit-to-default semantics.
  advancedUploadOptions: {
    chunkWordSize: null,
    chunkOverlapWordSize: null,
    delimiter: null,
  },
});

export const state = createInitialState();

export const getters = {
  accountId: state => state.accountId,
  datasetId: state => state.datasetId,
  stagedFiles: state => state.stagedFiles,
  hasStagedFiles: state => state.stagedFiles.length > 0,
  documents: state => state.documents,
  isCreatingDataset: state => state.isCreatingDataset,
  isUploading: state => state.isUploading,
  isError: state => state.isError,
  discardedFailures: state => state.discardedFailures,
  hasReadyDocument: state =>
    state.documents.some(doc => doc.status === 'ready'),
  droppedDocuments: state =>
    state.documents.filter(doc => doc.status === 'dropped'),
  readyCount: state =>
    state.documents.filter(doc => doc.status === 'ready').length,
  totalCount: state => state.documents.length,
  // The API reports no indexing percentage, so this is a weighted estimate of
  // the stages we do know about, not a measured figure: upload counts for the
  // first 30% of a file, queued/indexing for the rest, done is 100%.
  overallProgress: state => {
    if (!state.documents.length) return 0;
    const weightOf = doc => {
      if (doc.status === 'uploading') return 0.3 * (doc.uploadProgress || 0);
      if (doc.status === 'indexing') return 0.6;
      return 1; // ready, failed, timed_out are all settled
    };
    const total = state.documents.reduce((sum, doc) => sum + weightOf(doc), 0);
    return total / state.documents.length;
  },
  // One value the processing modal switches on:
  //   idle      - nothing in flight, nothing to show
  //   working   - creating, uploading or indexing: modal is blocking
  //   success   - every document is ready
  //   partial   - some ready, some failed/timed out
  //   timedOut  - nothing ready yet and polling gave up on at least one
  //   allFailed - nothing succeeded (the dataset was auto-deleted, or
  //               deleting it failed and it is still around)
  processingPhase: state => {
    if (!state.datasetId && state.discardedFailures.length) return 'allFailed';
    if (state.isCreatingDataset || state.isUploading) return 'working';
    if (!state.datasetId || !state.documents.length) return 'idle';
    const count = status =>
      state.documents.filter(doc => doc.status === status).length;
    if (count('uploading') + count('indexing') > 0) return 'working';
    const ready = count('ready');
    const failed = count('failed');
    const timedOut = count('timed_out');
    if (ready > 0 && !failed && !timedOut) return 'success';
    if (ready > 0) return 'partial';
    return timedOut > 0 ? 'timedOut' : 'allFailed';
  },
  knowledgeGraphEnabled: state => state.knowledgeGraphEnabled,
  knowledgeImagesEnabled: state => state.knowledgeImagesEnabled,
  advancedUploadOptions: state => state.advancedUploadOptions,
};

export const mutations = {
  SET_ACCOUNT_ID(state, accountId) {
    state.accountId = accountId;
  },
  RESET_STATE(state) {
    Object.assign(state, createInitialState());
  },
  SET_DATASET_ID(state, id) {
    state.datasetId = id;
  },
  SET_STAGED_FILES(state, files) {
    state.stagedFiles = files;
  },
  REMOVE_STAGED_FILE_AT(state, index) {
    state.stagedFiles = state.stagedFiles.filter((_, i) => i !== index);
  },
  SET_DOCUMENTS(state, documents) {
    state.documents = documents;
  },
  SET_DISCARDED_FAILURES(state, documents) {
    state.discardedFailures = documents;
  },
  ADD_DOCUMENT(state, document) {
    state.documents.push(document);
  },
  UPDATE_DOCUMENT(state, { id, patch }) {
    const index = state.documents.findIndex(doc => doc.id === id);
    if (index !== -1) {
      state.documents.splice(index, 1, { ...state.documents[index], ...patch });
    }
  },
  SET_CREATING_DATASET(state, bool) {
    state.isCreatingDataset = bool;
  },
  SET_UPLOADING(state, bool) {
    state.isUploading = bool;
  },
  SET_ERROR(state, bool) {
    state.isError = bool;
  },
  SET_GRAPH_ENABLED(state, bool) {
    state.knowledgeGraphEnabled = bool;
  },
  SET_IMAGES_ENABLED(state, bool) {
    state.knowledgeImagesEnabled = bool;
  },
  SET_ADVANCED_UPLOAD_OPTIONS(state, options) {
    state.advancedUploadOptions = {
      ...state.advancedUploadOptions,
      ...options,
    };
  },
  RESET_ADVANCED_UPLOAD_OPTIONS(state) {
    state.advancedUploadOptions = {
      chunkWordSize: null,
      chunkOverlapWordSize: null,
      delimiter: null,
    };
  },
};

export const actions = {
  // Only stages files — deliberately does not call createDataset/uploadDocuments.
  // The options step is what turns staged files into a real dataset, so
  // graph_enabled/images_enabled (creation-time-only) can still be changed
  // by the time that happens.
  // Picking a file that's already staged (same name, size and modified time)
  // is skipped rather than staged twice, otherwise every copy would be
  // uploaded on the next step. Returns the skipped files so the caller can
  // tell the user.
  stageFiles({ commit, state }, files) {
    const keyOf = file => `${file.name}|${file.size}|${file.lastModified}`;
    const seen = new Set(state.stagedFiles.map(keyOf));
    const added = [];
    const duplicates = [];
    Array.from(files).forEach(file => {
      const key = keyOf(file);
      if (seen.has(key)) {
        duplicates.push(file);
        return;
      }
      seen.add(key);
      added.push(file);
    });
    if (added.length) {
      commit('SET_STAGED_FILES', [...state.stagedFiles, ...added]);
    }
    return { added, duplicates };
  },

  removeStagedFileAt({ commit }, index) {
    commit('REMOVE_STAGED_FILE_AT', index);
  },

  updateGraphEnabled({ commit }, enabled) {
    commit('SET_GRAPH_ENABLED', enabled);
  },

  updateImagesEnabled({ commit }, enabled) {
    commit('SET_IMAGES_ENABLED', enabled);
  },

  updateAdvancedUploadOptions({ commit }, options) {
    commit('SET_ADVANCED_UPLOAD_OPTIONS', options);
  },

  // Called by the onboarding step components on mount/account change.
  // A no-op the first time this account is seen (state.accountId is still
  // null) or when the account hasn't actually changed, so it's safe to call
  // on every mount without wiping in-progress state for the same account.
  resetIfAccountChanged({ state, commit, rootGetters }) {
    const accountId = rootGetters.getCurrentAccountId;
    if (state.accountId !== null && state.accountId !== accountId) {
      commit('RESET_STATE');
    }
    commit('SET_ACCOUNT_ID', accountId);
  },

  async createDataset({ commit, state, rootGetters }, name) {
    const accountId = rootGetters.getCurrentAccountId;
    // This account already has a dataset in this wizard run (e.g. a retry, or
    // one restored after a refresh) — reuse it. The Knowledge API accepts
    // duplicate names, so creating again would silently make a second copy
    // that indexes and bills separately.
    if (state.datasetId && state.accountId === accountId) {
      return state.datasetId;
    }
    commit('SET_DISCARDED_FAILURES', []);
    // Defense in depth alongside resetIfAccountChanged — a dataset created
    // for a previously-active account must never be reused for a new one.
    if (state.accountId !== accountId) {
      commit('RESET_STATE');
    }
    commit('SET_ACCOUNT_ID', accountId);
    commit('SET_CREATING_DATASET', true);
    commit('SET_ERROR', false);

    // The create endpoint has been observed 200-ing with a literal `null`
    // body (upstream bug, not this proxy — it forwards bytes verbatim) even
    // though the dataset was actually created, and a retry of the same
    // auto-derived name then hits a 409. Onboarding never lets the user
    // change that name, so without this lookup both cases permanently
    // stuck the wizard on the configure/upload step. List items expose the
    // id as `id` (confirmed via knowledge.js's fetchKnowledgeList/deleteKnowledge
    // usage), unlike the create response's `dataset_id`.
    const findExistingDatasetIdByName = async () => {
      try {
        const { data } = await axios.get(
          knowledgeProxy(accountId, `/chatbot/accounts/${accountId}/datasets`)
        );
        return (data?.data || []).find(dataset => dataset.name === name)?.id;
      } catch (listError) {
        return null;
      }
    };

    try {
      const { data } = await axios.post(
        knowledgeProxy(accountId, `/chatbot/accounts/${accountId}/datasets`),
        // Matches the standalone Knowledge feature's default (knowledge.js's
        // createKnowledgeBase) — onboarding has no engine-choice UI of its
        // own, so it always creates on lightrag, same as every other KB
        // creation path in the app defaults to.
        {
          name,
          engine: 'lightrag',
          graph_enabled: state.knowledgeGraphEnabled,
          images_enabled: state.knowledgeImagesEnabled,
        }
      );
      // Confirmed against a live response: the field is `dataset_id`, not
      // `id`. Before failing outright, check whether the dataset actually
      // got created upstream despite the broken response — see the lookup
      // helper above.
      const datasetId =
        data?.dataset_id || (await findExistingDatasetIdByName());
      if (!datasetId) {
        throw new Error(
          `Dataset creation response is missing a dataset_id. Got: ${JSON.stringify(
            data
          )}`
        );
      }
      commit('SET_DATASET_ID', datasetId);
      return datasetId;
    } catch (error) {
      // A 409 here almost always means the name-collision case above: this
      // exact retry already succeeded once. Recover it instead of dead-ending.
      if (error.response?.status === 409) {
        const existingId = await findExistingDatasetIdByName();
        if (existingId) {
          commit('SET_DATASET_ID', existingId);
          return existingId;
        }
      }
      commit('SET_ERROR', true);
      throw error.response?.data || error;
    } finally {
      commit('SET_CREATING_DATASET', false);
    }
  },

  async uploadDocuments({ commit, state, rootGetters, dispatch }, files) {
    const accountId = rootGetters.getCurrentAccountId;
    // A dataset left over from a different account must never be uploaded
    // into on this account's behalf — treat it the same as having no
    // dataset at all, forcing the caller back through createDataset.
    if (!state.datasetId || state.accountId !== accountId) {
      throw new Error(
        'uploadDocuments: missing datasetId — call createDataset first'
      );
    }

    const datasetId = state.datasetId;
    commit('SET_UPLOADING', true);

    const results = await files.reduce(async (previous, file) => {
      const acc = await previous;
      const succeeded = await uploadSingleFile({
        accountId,
        datasetId,
        file,
        stagedIndex: (state.stagedFiles || []).indexOf(file),
        // Read lazily: earlier uploads in this same loop add documents as it runs.
        existingDocumentIds: () =>
          (state.documents || []).map(doc => String(doc.id)),
        commit,
        dispatch,
        advancedUploadOptions: state.advancedUploadOptions,
      });
      return [...acc, succeeded];
    }, Promise.resolve([]));

    commit('SET_UPLOADING', false);
    // Every upload may have failed outright (402/413/network) — in that case
    // there is no poll left to notice, so check here.
    await dispatch('discardIfNothingSucceeded');
    return results.some(Boolean);
  },

  // Deletes the dataset when none of its documents succeeded, so a failed
  // run doesn't leave an empty knowledge base behind (and no retry path).
  // Only when EVERY document has definitively failed: a 'timed_out' document
  // may still finish, and while uploads are running a document that failed
  // early must not condemn files that haven't been added yet.
  async discardIfNothingSucceeded({ state, dispatch }) {
    if (!state.datasetId || state.isUploading || state.isCreatingDataset)
      return;
    if (!state.documents.length) return;
    // 'dropped' counts as "nothing came of it" too: a dataset whose only
    // outcomes are failures and dropped files has no usable document.
    if (
      !state.documents.every(doc => ['failed', 'dropped'].includes(doc.status))
    ) {
      return;
    }
    await dispatch('discardDataset', { keepFailures: true });
  },

  // Best-effort delete of the whole dataset. Local state is cleared first so
  // any poll still in flight stops (it bails when its document is gone), and a
  // failed DELETE never blocks the user — worst case an empty dataset remains.
  // keepFailures records the failed documents so the modal can still explain
  // what happened after the dataset itself is gone.
  async discardDataset(
    { commit, state, rootGetters },
    { keepFailures = false } = {}
  ) {
    const accountId = rootGetters.getCurrentAccountId;
    const datasetId = state.datasetId;
    if (!datasetId || state.accountId !== accountId) return;

    commit('SET_DISCARDED_FAILURES', keepFailures ? state.documents : []);
    commit('SET_DATASET_ID', null);
    commit('SET_DOCUMENTS', []);
    try {
      await axios.delete(
        knowledgeProxy(
          accountId,
          `/chatbot/accounts/${accountId}/datasets/${datasetId}`
        )
      );
    } catch (error) {
      // Nothing more to do — see comment above.
    }
  },

  // Re-uploads the documents that failed, from the Files still held in
  // stagedFiles. Not possible after a refresh (File objects don't survive) —
  // the modal only offers it when this returns something to retry.
  async retryFailedDocuments({ commit, state, dispatch }) {
    const failed = state.documents.filter(doc => doc.status === 'failed');
    const files = failed
      .map(doc => state.stagedFiles[doc.stagedIndex])
      .filter(Boolean);
    if (!files.length) return false;

    await dispatch('deleteDocuments', failed);
    commit(
      'SET_DOCUMENTS',
      state.documents.filter(doc => doc.status !== 'failed')
    );
    return dispatch('uploadDocuments', files);
  },

  // Drops failed documents so the user can continue with the ones that worked.
  async removeFailedDocuments({ commit, state, dispatch }) {
    const failed = state.documents.filter(doc => doc.status === 'failed');
    await dispatch('deleteDocuments', failed);
    commit(
      'SET_DOCUMENTS',
      state.documents.filter(doc => doc.status !== 'failed')
    );
  },

  // Best-effort server-side delete of documents that actually reached the
  // server (a failed upload only ever had a local placeholder id).
  async deleteDocuments({ state, rootGetters }, documents) {
    const accountId = rootGetters.getCurrentAccountId;
    await Promise.all(
      documents
        .filter(doc => !String(doc.id).startsWith('local-'))
        .map(async doc => {
          try {
            await axios.delete(
              knowledgeProxy(
                accountId,
                `/chatbot/datasets/${state.datasetId}/documents/${doc.id}`
              )
            );
          } catch (error) {
            // A leftover failed document is harmless; don't block on it.
          }
        })
    );
  },

  // "Keep waiting" — polling gave up but the document may still be indexing.
  resumePolling({ commit, state, dispatch }) {
    state.documents
      .filter(doc => doc.status === 'timed_out')
      .forEach(doc => {
        commit('UPDATE_DOCUMENT', {
          id: doc.id,
          patch: { status: 'indexing', error: null },
        });
        dispatch('pollDocumentStatus', { localId: doc.id });
      });
  },

  // Rebuilds the wizard's view of a dataset after a page refresh from what
  // the server reports, so the user isn't sent back to step 1 (where
  // re-picking files would create a second dataset). `session` is the record
  // from helper/onboardingKbSession.js. Returns false if the dataset no longer
  // exists or has nothing in it, so the caller can fall back to step 1.
  async restoreFromSession(
    { commit, rootGetters, dispatch },
    { datasetId, docs = [], staged = [], dropped = [] }
  ) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const { data } = await axios.get(
        knowledgeProxy(
          accountId,
          `/chatbot/accounts/${accountId}/datasets/${datasetId}/documents?limit=100`
        )
      );
      const list = data?.data || data?.documents || [];
      if (!list.length) return false;

      const documents = list.map(item => {
        let status = 'indexing';
        if (item.display_status === 'available') status = 'ready';
        else if (
          ['error', 'unavailable', 'disabled'].includes(item.display_status)
        ) {
          status = 'failed';
        }
        return {
          id: String(item.id),
          name: item.name,
          size: docs.find(doc => String(doc.id) === String(item.id))?.size,
          uploadProgress: 1,
          status,
          error:
            status === 'failed' ? 'Indexing failed for this document.' : null,
        };
      });
      // Anything the user chose that the server no longer lists must still be
      // shown, never silently omitted. A document we remember uploading (it
      // has a saved id) that is now missing was removed server-side - the
      // Knowledge service drops a file whose contents duplicate another. A
      // chosen file we never saw an id for was cut off by the refresh before
      // its upload finished.
      const listedIds = new Set(documents.map(doc => doc.id));
      const removed = docs
        .filter(doc => !listedIds.has(String(doc.id)))
        .map(doc => ({
          id: String(doc.id),
          name: doc.name,
          size: doc.size,
          uploadProgress: 1,
          status: 'dropped',
          reason: 'removed',
          error: 'This file is no longer in your knowledge base.',
        }));
      // Files already reported as dropped before an earlier refresh stay
      // reported - they are no longer in docs/staged, so without this a second
      // refresh would make the notice vanish.
      const REASON_TEXT = {
        duplicate: 'Same content as another file you added.',
        removed: 'This file is no longer in your knowledge base.',
        not_uploaded: 'The upload was interrupted before this file finished.',
      };
      const earlierDropped = dropped.map(file => ({
        id: `dropped-${file.name}`,
        name: file.name,
        size: file.size,
        uploadProgress: file.reason === 'removed' ? 1 : 0,
        status: 'dropped',
        reason: file.reason,
        error: REASON_TEXT[file.reason],
      }));
      // Match chosen files to what is accounted for by name, one for one, so
      // two files with the same name are not both considered present.
      const accountedNames = [...documents, ...removed, ...earlierDropped].map(
        doc => doc.name
      );
      const notUploaded = staged
        .filter(file => {
          const at = accountedNames.indexOf(file.name);
          if (at === -1) return true;
          accountedNames.splice(at, 1);
          return false;
        })
        .map(file => ({
          id: `not-uploaded-${file.name}`,
          name: file.name,
          size: file.size,
          uploadProgress: 0,
          status: 'dropped',
          reason: 'not_uploaded',
          error: 'The upload was interrupted before this file finished.',
        }));

      commit('SET_ACCOUNT_ID', accountId);
      commit('SET_DATASET_ID', datasetId);
      commit('SET_DOCUMENTS', [
        ...documents,
        ...removed,
        ...earlierDropped,
        ...notUploaded,
      ]);
      documents
        .filter(doc => doc.status === 'indexing')
        .forEach(doc => dispatch('pollDocumentStatus', { localId: doc.id }));
      await dispatch('discardIfNothingSucceeded');
      return true;
    } catch (error) {
      return false;
    }
  },

  // Leaves the "everything failed" outcome so the wizard goes back to an
  // ordinary step 2 (files still staged, options editable).
  clearDiscardedFailures({ commit }) {
    commit('SET_DISCARDED_FAILURES', []);
  },

  async pollDocumentStatus(
    { commit, state, rootGetters, dispatch },
    { localId, attempt = 0 }
  ) {
    const accountId = rootGetters.getCurrentAccountId;
    // The account may have changed since this poll chain was started (or a
    // stale chain from a previous account could still be ticking) — stop
    // rather than keep polling/reporting status for another account's doc.
    if (state.accountId !== accountId) return;

    const doc = state.documents.find(d => d.id === localId);
    if (!doc || doc.status !== 'indexing') return;

    if (attempt >= MAX_POLL_ATTEMPTS) {
      // Not 'failed': the document may still finish server-side, so the user
      // is offered "keep waiting" and nothing is deleted.
      commit('UPDATE_DOCUMENT', {
        id: localId,
        patch: { status: 'timed_out', error: null },
      });
      return;
    }

    try {
      const { data } = await axios.get(
        knowledgeProxy(
          accountId,
          `/chatbot/datasets/${state.datasetId}/documents/${localId}/details`
        )
      );

      if (isIndexingComplete(data)) {
        commit('UPDATE_DOCUMENT', { id: localId, patch: { status: 'ready' } });
        return;
      }
      if (isIndexingFailed(data)) {
        commit('UPDATE_DOCUMENT', {
          id: localId,
          patch: {
            status: 'failed',
            error: 'Indexing failed for this document.',
          },
        });
        await dispatch('discardIfNothingSucceeded');
        return;
      }
    } catch (error) {
      // A 404 means the server no longer has this document (the Knowledge
      // service drops a file whose contents duplicate another one) —
      // retrying for two minutes would only hide that from the user. It is
      // 'dropped', not 'failed': nothing is wrong with the file, so it is
      // not retryable and must not read as an error. Anything else is
      // treated as transient and retried until MAX_POLL_ATTEMPTS.
      if (error.response?.status === 404) {
        commit('UPDATE_DOCUMENT', {
          id: localId,
          patch: {
            status: 'dropped',
            reason: 'removed',
            error: 'This file is no longer in your knowledge base.',
          },
        });
        await dispatch('discardIfNothingSucceeded');
        return;
      }
    }

    setTimeout(() => {
      dispatch('pollDocumentStatus', { localId, attempt: attempt + 1 });
    }, POLL_INTERVAL_MS);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
